import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  SidePanelType,
  ButtonPrimary,
  ButtonPseudo,
  ajvResolver,
  SidePanelLoader,
  usePrevious,
  responseErrorsResolver,
  ResponseError,
  PermissionEnum,
  Popup,
  PermissionDenied,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import useGetAutocompleteList from '../../store/dictionaries/helpers/use-accounts-autocomplete';
import { clearFeeFormState } from './store/slices/form';
import {
  FeeItem,
  FeeItemFormValues,
  FeeSubTypeEnum,
  FeeTransactionTypeEnum,
  FeeTypeEnum,
} from './types';
import {
  useCreateFeeItemMutation,
  useUpdateFeeItemMutation,
  useLazyGetFeeItemQuery,
  useLazyGetFeesListQuery,
} from './store/services';
import Form from '../../components/fees/form';

import { FeesRoutes } from '../../routes';

const schema = {
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 1,
      errorMessage: { minLength: 'Fee type name is required.' },
    },
    feeType: {
      type: 'string',
      minLength: 1,
      errorMessage: { minLength: 'Work method is required.' },
    },
    feeTargetAccountNumber: {
      type: 'string',
      minLength: 1,
      errorMessage: { minLength: 'Target account is required.' },
    },
  },
  required: ['name', 'feeType', 'feeTargetAccountNumber'],
};

const FeeForm = () => {
  const {
    fees: {
      form: { formValues },
    },
    configurationDictionaries: { activePermissions, paymentTypes },
  } = useAppSelector((state) => state);

  const { feeId } = useParams();
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [defaultFormValues, setDefaultFormValues] = useState<FeeItemFormValues>(
    { name: '', feeType: undefined, feeTargetAccountNumber: '' },
  );
  const [formattedSchema, setFormattedSchema] = useState(schema);
  const [globalError, setGlobalError] = useState('');

  const {
    control,
    watch,
    reset,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FeeItemFormValues>({
    resolver: ajvResolver(formattedSchema),
    defaultValues: { name: '', feeType: undefined, feeTargetAccountNumber: '' },
  });

  const fields = watch();

  const isDirty = !isEqual(defaultFormValues, fields);

  const workMethod = watch('feeType');
  const condition = watch('regularFeeSubType');
  const name = watch('name');
  const feeTransactionType = watch('feeTransactionType');
  const prevWorkMethod = usePrevious(workMethod);
  const prevTransactionType = usePrevious(feeTransactionType);

  const [panelIsOpen, setPanelIsOpen] = useState(false);

  const [getFeesList] = useLazyGetFeesListQuery();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    dispatch(clearFeeFormState());

    setTimeout(() => {
      navigate(FeesRoutes.FEES_LIST);
    }, 100);
  }, []);

  const {
    accounts,
    onSearch,
    isLoading: accountsIsLoading,
    isFetching: accountsIsFetching,
  } = useGetAutocompleteList(feeId);
  const [create, { isLoading: requestInProgress, isSuccess }] =
    useCreateFeeItemMutation();
  const [update, { isLoading: updateInProgress, isSuccess: updateIsSuccess }] =
    useUpdateFeeItemMutation();
  const [getFee, { isLoading, isFetching }] = useLazyGetFeeItemQuery();

  const getFeeAction = useCallback(async () => {
    const response = (await getFee(feeId as string)) as {
      error?: ResponseError;
    };

    if (response?.error) {
      if (response?.error?.data?.error) {
        if (
          typeof response?.error?.data?.error === 'string' &&
          !response?.error?.data?.message
        ) {
          setGlobalError(response?.error?.data?.error);
        } else if (
          response?.error?.data?.message &&
          typeof response?.error?.data?.message === 'string'
        ) {
          setGlobalError(response?.error?.data?.message);
        } else if (
          response?.error?.data?.message &&
          Array.isArray(response?.error?.data?.message)
        ) {
          const messages: string[] = [];

          response?.error?.data?.message?.forEach((item) => {
            messages.push(item.value as string);
          });

          setGlobalError(messages?.join(', '));
        } else {
          setGlobalError('Internal Server Error. Try again.');
        }
      } else if (response?.error?.status === 500) {
        setGlobalError('Internal Server Error. Try again.');
      }
    }
  }, [feeId]);

  const onFormSubmit = handleSubmit(async (values) => {
    setGlobalError('');

    if (
      values.feeType === FeeTypeEnum.TRANSACTION &&
      (values.feeTransactionType === 'OUTGOING' ||
        values.feeTransactionType === 'INCOMING') &&
      !values.paymentType
    ) {
      setError('paymentType', {
        type: 'custom',
        message: 'Payment type is required',
      });
    } else {
      const body: FeeItemFormValues = {
        name: values.name,
        feeType: values.feeType,
      };

      if (values?.feeTargetAccountNumber?.length) {
        body.feeTargetAccountNumber = values.feeTargetAccountNumber;
      }

      if (values.regularInactivityMonthsAmount) {
        body.regularInactivityMonthsAmount = Number(
          values.regularInactivityMonthsAmount,
        );
      }

      if (values.regularFeeSubType && !feeId) {
        body.regularFeeSubType = values.regularFeeSubType;
      }

      if (values.regularHighBalanceAmount && !feeId) {
        body.regularHighBalanceAmount = Number(values.regularHighBalanceAmount);
      }

      if (values.paymentType) {
        body.paymentType = values.paymentType;
      }

      if (values.feeTransactionType) {
        body.feeTransactionType = values.feeTransactionType;
      }

      let response;

      if (feeId) {
        response = (await update({ body, id: feeId })) as {
          data?: FeeItem;
          error: ResponseError;
        };
      } else {
        response = (await create(body)) as {
          data?: FeeItem;
          error: ResponseError;
        };
      }

      if (response?.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
          setGlobalError,
        });
      }
    }
  });

  useEffect(() => {
    let nextSchema: any = schema;

    if (workMethod !== prevWorkMethod) {
      nextSchema = schema;
    }

    if (workMethod === FeeTypeEnum.TRANSACTION) {
      const baseNextSchema = {
        ...schema,
        properties: {
          ...schema.properties,
          feeTransactionType: {
            type: 'string',
            minLength: 1,
            errorMessage: { minLength: 'Transaction is required.' },
          },
        },
        required: [...schema.required, 'feeTransactionType'],
      };

      nextSchema = baseNextSchema;

      if (
        feeTransactionType === FeeTransactionTypeEnum.INCOMING ||
        feeTransactionType === FeeTransactionTypeEnum.OUTGOING
      ) {
        nextSchema = {
          ...baseNextSchema,
          properties: {
            ...baseNextSchema.properties,
            paymentType: {
              type: 'string',
              minLength: 1,
              errorMessage: { minLength: 'Payment type is required.' },
            },
          },
          required: [...baseNextSchema.required, 'paymentType'],
        };
      } else if (
        (prevTransactionType === FeeTransactionTypeEnum.INCOMING ||
          prevTransactionType === FeeTransactionTypeEnum.OUTGOING) &&
        feeTransactionType === FeeTransactionTypeEnum.INTERNAL
      ) {
        nextSchema = baseNextSchema;
      }
    } else if (workMethod === FeeTypeEnum.REGULAR) {
      const baseNextSchema = {
        ...schema,
        properties: {
          ...schema.properties,
          regularFeeSubType: {
            type: 'string',
            minLength: 1,
            errorMessage: { minLength: 'Fee Sub type is required.' },
          },
        },
        required: [...schema.required, 'regularFeeSubType'],
      };

      nextSchema = baseNextSchema;

      if (condition === FeeSubTypeEnum.INACTIVITY) {
        nextSchema = {
          ...baseNextSchema,
          properties: {
            ...baseNextSchema.properties,
            regularInactivityMonthsAmount: {
              type: 'string',
              minLength: 1,
              errorMessage: { minLength: 'Inactivity months is required.' },
            },
          },
          required: [
            ...baseNextSchema.required,
            'regularInactivityMonthsAmount',
          ],
        };
      } else if (condition === FeeSubTypeEnum.HIGH_BALANCE) {
        nextSchema = {
          ...baseNextSchema,
          properties: {
            ...baseNextSchema.properties,
            regularHighBalanceAmount: {
              type: 'string',
              minLength: 1,
              errorMessage: { minLength: 'High balance amount is required.' },
            },
          },
          required: [...baseNextSchema.required, 'regularHighBalanceAmount'],
        };
      } else if (condition === FeeSubTypeEnum.REGULAR) {
        nextSchema = baseNextSchema;
      }
    }

    setFormattedSchema(nextSchema);
  }, [
    schema,
    workMethod,
    feeTransactionType,
    prevTransactionType,
    condition,
    prevWorkMethod,
  ]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
      getFeesList();
    }
  }, [isSuccess]);

  useEffect(() => {
    setPanelIsOpen(true);

    if (feeId) {
      getFeeAction();
    }
  }, [getFeeAction, feeId]);

  useEffect(() => {
    if (prevWorkMethod && workMethod && prevWorkMethod !== workMethod) {
      reset({ name, feeType: workMethod });
    }
  }, [reset, workMethod, prevWorkMethod, name]);

  useEffect(() => {
    if (formValues && feeId) {
      reset(formValues);
      setDefaultFormValues(formValues);
    }
  }, [reset, formValues, feeId]);

  useEffect(() => {
    if (updateIsSuccess) {
      setDefaultFormValues(fields);
    }
  }, [updateIsSuccess]);

  let content;
  let buttonText;
  let title;

  if (isLoading || isFetching || accountsIsLoading) {
    content = <SidePanelLoader />;
  } else if (
    !activePermissions?.includes(PermissionEnum.FEES_TYPES_CREATE_ACTION) &&
    !feeId
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <Form
        control={control}
        workMethod={workMethod}
        condition={condition}
        errors={errors}
        feeId={feeId}
        feeTransactionType={feeTransactionType}
        isEditable={activePermissions?.includes(
          PermissionEnum.FEES_TYPES_EDIT_ACTION,
        )}
        isLoadingSearch={accountsIsLoading || accountsIsFetching}
        onSearch={onSearch}
        accounts={accounts}
        paymentTypes={paymentTypes?.map((item) => ({
          name: item.name,
          value: item.paymentType,
        }))}
        auditLogAvailable={activePermissions?.includes(
          PermissionEnum.AUDIT_LOG_LIST,
        )}
      />
    );
  }

  if (requestInProgress || updateInProgress) {
    buttonText = 'Loading...';
  } else {
    buttonText = feeId ? 'Save' : 'Create';
  }

  if (feeId) {
    title = 'Edit fee type';

    if (!activePermissions?.includes(PermissionEnum.FEES_TYPES_EDIT_ACTION)) {
      title = 'Fee type info';
    }
  } else {
    title = 'Add fee type';
  }

  const buttonVisile = feeId
    ? activePermissions?.includes(PermissionEnum.FEES_TYPES_EDIT_ACTION)
    : activePermissions?.includes(PermissionEnum.FEES_TYPES_CREATE_ACTION);

  return (
    <>
      <SidePanel
        type={SidePanelType.Fees}
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={title}
        errorSidepanel={globalError}
        sidepanelExpandedKey="fee"
        footer={
          <>
            {buttonVisile && (
              <ButtonPrimary
                text={buttonText}
                disabled={Boolean(
                  !workMethod || !name || requestInProgress || updateInProgress,
                )}
                onClick={onFormSubmit}
                bold
              />
            )}

            <ButtonPseudo
              text="Close"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        {content}
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default FeeForm;
